import { Model } from '@models'
import { ACTIVE_NOTIFICATIONS } from '@src/graphql/activeNotifications'
import { BULK_MARK_NOTIFICATIONS_READ } from '@src/graphql/bulkMarkNotificationsRead'
import { COMPLETE_NOTIFICATION } from '@src/graphql/completeNotification'
import { COMPLETE_LIVE_NOTIFICATION } from '@src/graphql/completeLiveNotification'
import { DISMISS_NOTIFICATION } from '@src/graphql/dismissNotification'
import { DISMISS_LIVE_NOTIFICATION } from '@src/graphql/dismissLiveNotification'
import { LIVE_NOTIFICATIONS } from '@src/graphql/liveNotifications'
import { LiveNotificationsQuery } from '@src/graphql/liveNotifications.generated'
import { MARK_ALL_NOTIFICATIONS_READ } from '@src/graphql/markAllNotificationsRead'
import { MARK_NOTIFICATION_READ } from '@src/graphql/markNotificationRead'
import { NOTIFICATION_INBOX } from '@src/graphql/notificationInbox'
import { NotificationInboxQuery } from '@src/graphql/notificationInbox.generated'
import { waitForFlagsInitialization } from '@src/models/helper'
import { NotificationRecord } from '@src/models/notifications/types'
import { NotificationInboxStateFilter } from '@src/types'
import { getNotificationRefetchActions, isNotificationInboxEnabled } from './refetch'
import reducers from './reducers'
import { NotificationsStoreState } from './types'

const markNotificationsFetchedReducer = {
  name: 'markFetched',
  payload: () => new Date().toISOString(),
}

export default class Notifications {
  namespace = 'notifications'

  state: NotificationsStoreState = {
    ...Model.defaultState,
    lastFetchedAt: null,

    // Legacy 'all active' notifications
    notifications: [],

    // Live surface notifications
    liveNotifications: [],

    inbox: {
      unreadCount: 0,
      notifications: {},
    },
  }

  effects = {
    fetch: Model.buildEffect({
      name: `${this.namespace}/fetch`,
      query: ACTIVE_NOTIFICATIONS,
      dataPath: 'activeNotifications',
      reducers: [
        {
          name: 'fetchList',
          storePath: () => 'notifications',
          dataPath: 'notifications',
        },
        markNotificationsFetchedReducer,
      ],
    }),
    fetchLive: Model.buildEffect({
      name: `${this.namespace}/fetchLive`,
      query: LIVE_NOTIFICATIONS,
      dataPath: 'liveNotifications',
      reducers: [
        {
          name: 'setLiveNotifications',
          payload: (response: LiveNotificationsQuery['liveNotifications']) =>
            response.notifications,
        },
        markNotificationsFetchedReducer,
      ],
    }),
    refetch: Model.buildEffect({
      name: `${this.namespace}/refetch`,
      *onSuccess(
        _: unknown,
        {
          call,
          put,
        }: {
          call: (fn: (...args: any[]) => unknown, ...args: unknown[]) => unknown
          put: (action: { type: string }) => void
        },
      ) {
        yield call(waitForFlagsInitialization)
        const actions = getNotificationRefetchActions(isNotificationInboxEnabled())

        for (const type of actions) {
          yield put({ type })
        }
      },
    }),
    fetchInbox: Model.buildEffect({
      name: `${this.namespace}/fetchInbox`,
      query: NOTIFICATION_INBOX,
      dataPath: 'notificationInbox',
      variables: (payload?: { first?: number; stateFilter?: NotificationInboxStateFilter }) => ({
        first: 25,
        ...payload,
      }),
      reducers: [
        {
          name: 'storeInboxPage',
          payload: (
            response: NotificationInboxQuery['notificationInbox'],
            { effectPayload }: { effectPayload?: { stateFilter?: NotificationInboxStateFilter } },
          ) => ({
            stateFilter: effectPayload?.stateFilter || NotificationInboxStateFilter.All,
            records: response.notifications,
            nextCursor: response.nextCursor,
          }),
        },
        { name: 'setInboxUnreadCount' },
        markNotificationsFetchedReducer,
      ],
    }),
    fetchInboxNextPage: Model.buildEffect({
      name: `${this.namespace}/fetchInboxNextPage`,
      query: NOTIFICATION_INBOX,
      dataPath: 'notificationInbox',
      variables: (payload: {
        after: string
        first?: number
        stateFilter?: NotificationInboxStateFilter
      }) => ({
        first: 25,
        ...payload,
      }),
      reducers: [
        {
          name: 'storeInboxPage',
          payload: (
            response: NotificationInboxQuery['notificationInbox'],
            { effectPayload }: { effectPayload: { stateFilter?: NotificationInboxStateFilter } },
          ) => ({
            stateFilter: effectPayload.stateFilter || NotificationInboxStateFilter.All,
            records: response.notifications,
            nextCursor: response.nextCursor,
            append: true,
          }),
        },
        { name: 'setInboxUnreadCount' },
        markNotificationsFetchedReducer,
      ],
    }),
    fetchInboxSummary: Model.buildEffect({
      name: `${this.namespace}/fetchInboxSummary`,
      query: NOTIFICATION_INBOX,
      dataPath: 'notificationInbox',
      variables: (payload?: { first?: number }) => ({
        first: 1,
        ...payload,
      }),
      reducers: [{ name: 'setInboxUnreadCount' }, markNotificationsFetchedReducer],
    }),
    markRead: Model.buildEffect({
      name: `${this.namespace}/markRead`,
      query: MARK_NOTIFICATION_READ,
      dataPath: 'markNotificationRead',
      reducers: [{ name: 'updateNotificationRecord' }],
    }),
    markAllRead: Model.buildEffect({
      name: `${this.namespace}/markAllRead`,
      query: MARK_ALL_NOTIFICATIONS_READ,
      dataPath: 'markAllNotificationsRead',
      isSuccess: (response: { success?: boolean }) => !!response.success,
    }),
    bulkMarkRead: Model.buildEffect({
      name: `${this.namespace}/bulkMarkRead`,
      query: BULK_MARK_NOTIFICATIONS_READ,
      dataPath: 'bulkMarkNotificationsRead',
      isSuccess: (response: { success?: boolean }) => !!response.success,
      reducers: [
        {
          name: 'markNotificationsRead',
          payload: (
            _: { success?: boolean },
            { effectPayload }: { effectPayload: { notificationIds: NotificationRecord['id'][] } },
          ) => ({
            // Server returns only success, so we stamp readAt locally to update UI immediately.
            notificationIds: effectPayload.notificationIds,
            readAt: new Date().toISOString(),
          }),
        },
      ],
    }),
    complete: Model.buildEffect({
      name: `${this.namespace}/complete`,
      query: COMPLETE_NOTIFICATION,
      dataPath: 'completeNotification',
      reducers: [{ name: 'updateNotificationRecord' }, { name: 'removeNotification' }],
    }),
    completeLive: Model.buildEffect({
      name: `${this.namespace}/completeLive`,
      query: COMPLETE_LIVE_NOTIFICATION,
      dataPath: 'completeLiveNotification',
      reducers: [
        {
          name: 'updateNotificationRecord',
          payload: (response: { notification: NotificationRecord | null }) => response.notification,
          unless: (response: { notification: NotificationRecord | null }) => !response.notification,
        },
        {
          name: 'removeNotification',
          payload: (response: { notification: NotificationRecord | null }) => ({
            id: response.notification!.id,
          }),
          unless: (response: { notification: NotificationRecord | null }) => !response.notification,
        },
        {
          name: 'removeLiveNotification',
          payload: (response: { id: string }) => ({ id: response.id }),
        },
      ],
    }),
    dismiss: Model.buildEffect({
      name: `${this.namespace}/dismiss`,
      query: DISMISS_NOTIFICATION,
      dataPath: 'dismissNotification',
      reducers: [{ name: 'updateNotificationRecord' }, { name: 'removeNotification' }],
    }),
    dismissLive: Model.buildEffect({
      name: `${this.namespace}/dismissLive`,
      query: DISMISS_LIVE_NOTIFICATION,
      dataPath: 'dismissLiveNotification',
      reducers: [
        {
          name: 'updateNotificationRecord',
          payload: (response: { notification: NotificationRecord | null }) => response.notification,
          unless: (response: { notification: NotificationRecord | null }) => !response.notification,
        },
        {
          name: 'removeNotification',
          payload: (response: { notification: NotificationRecord | null }) => ({
            id: response.notification!.id,
          }),
          unless: (response: { notification: NotificationRecord | null }) => !response.notification,
        },
        {
          name: 'removeLiveNotification',
          payload: (response: { id: string }) => ({ id: response.id }),
        },
      ],
    }),
  }

  reducers = reducers
}
